import { useState, useEffect } from "react";
import { getToken } from "../../services/tokenGET";

/**
 * Custom hook for managing species data and CRUD operations
 * Handles fetching, creating, updating, and deleting species
 * 
 * @param {Array} initialSpecies - Initial species data from API
 * @param {Array} areas - Areas data for zone mapping
 * @returns {Object} Species data and handlers
 */
export function useSpeciesData(initialSpecies, areas) {
    const [speciesList, setSpeciesList] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [newSpecieData, setNewSpecieData] = useState({
        name: "",
        category_name: "",
        description: "",
        habitat: ""
    });
    const [speciesAreaMap, setSpeciesAreaMap] = useState({});
    const [selectedSpeciesZones, setSelectedSpeciesZones] = useState([]);

    // Initialize species list
    useEffect(() => {
        if (initialSpecies) {
            setSpeciesList(initialSpecies);
        }
    }, [initialSpecies]);

    // Fetch species-area relationships
    useEffect(() => {
        if (areas && areas.length > 0) {
            const fetchSpeciesForAreas = async () => {
                const areaSpeciesMap = {};
                
                for (const area of areas) {
                    try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${area.id}`);
                        const areaSpecies = await response.json();
                        
                        areaSpecies.forEach(specie => {
                            if (!areaSpeciesMap[specie.id]) {
                                areaSpeciesMap[specie.id] = [];
                            }
                            areaSpeciesMap[specie.id].push(area.id);
                        });
                    } catch (error) {
                        console.error(`Error fetching species for area ${area.id}:`, error);
                    }
                }
                
                setSpeciesAreaMap(areaSpeciesMap);
            };
            
            fetchSpeciesForAreas();
        }
    }, [areas]);

    const handleSelectSpecies = (species) => {
        setNewSpecieData({
            name: species.name,
            description: species.description,
            category_name: species.category_name,
        });
        setSelectedItem(species.id);
        
        // Load zones for this species
        const specieZones = speciesAreaMap[species.id] || [];
        setSelectedSpeciesZones(specieZones);
    };

    const handleResetForm = () => {
        setNewSpecieData({
            name: "",
            category_name: "",
            description: "",
            habitat:"",
        });
        setSelectedItem(null);
        setSelectedSpeciesZones([]);
    };

    const handleSaveSpecies = async () => {
        const updatedSpecies = {
            name: newSpecieData.name,
            category_name: newSpecieData.category_name,
            description: newSpecieData.description,
            habitat: newSpecieData.habitat,
            areas: selectedSpeciesZones
        };

        const url = selectedItem
            ? `${process.env.REACT_APP_API_URL}/api/species/${selectedItem}`
            : `${process.env.REACT_APP_API_URL}/api/species`;

        try {
            const token = await getToken();
            const response = await fetch(url, {
                method: "POST",
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': token
                },
                body: JSON.stringify(updatedSpecies),
            });

            if (!response.ok) {
                alert("Error adding the specie!");
                return;
            }

            const data = await response.json();
            let speciesId = selectedItem;

            if (!selectedItem && data && data.species) {
                speciesId = data.species.id;
                setSpeciesList([...speciesList, data.species]);
            } else if (selectedItem) {
                setSpeciesList(speciesList.map(specie =>
                    specie.id === selectedItem ? { ...specie, ...updatedSpecies } : specie
                ));
            }

            /*
            // Save zone assignments
            if (speciesId && selectedSpeciesZones.length > 0) {
                for (const areaId of selectedSpeciesZones) {
                    try {
                        await fetch(`${process.env.REACT_APP_API_URL}/api/species/areas/${areaId}/species`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-CSRF-TOKEN': token
                            },
                            body: JSON.stringify({ species_ids: [speciesId] })
                        });
                    } catch (error) {
                        console.error(`Error assigning species to area ${areaId}:`, error);
                    }
                }
            }*/

            alert(selectedItem ? "Art uppdaterad!" : "Art tillagd!");
            handleResetForm();
        } catch (error) {
            console.error("Error adding new specie:", error);
            alert("Ett fel uppstod");
        }
    };

    const handleDeleteSpecies = async () => {
        if (!window.confirm("Är du säker på att du vill radera denna art?")) {
            return;
        }

        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${selectedItem}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                    'X-CSRF-TOKEN': token
                }
            });

            if (response.ok) {
                const updatedSpeciesList = speciesList.filter(specie => specie.id !== selectedItem);
                setSpeciesList(updatedSpeciesList);
                handleResetForm();
                alert("Arten har blivit raderad");
            } else {
                console.error(`Failed to delete species: ${response.status}`);
                alert("Kunde inte radera arten");
            }
        } catch (error) {
            console.error("Error deleting specie:", error);
            alert("Ett fel uppstod vid radering");
        }
    };

    const handleZoneToggle = (zoneId) => {
        setSelectedSpeciesZones(prev => {
            if (prev.includes(zoneId)) {
                return prev.filter(id => id !== zoneId);
            } else {
                return [...prev, zoneId];
            }
        });
    };

    return {
        speciesList,
        selectedItem,
        newSpecieData,
        setNewSpecieData,
        speciesAreaMap,
        selectedSpeciesZones,
        setSelectedSpeciesZones,
        handleSelectSpecies,
        handleResetForm,
        handleSaveSpecies,
        handleDeleteSpecies,
        handleZoneToggle,
    };
}
